/* Release-date ordering is computed by the NMREL shop index. Product cards
 * themselves are always Shopify-rendered Liquid, never approximated in JS.
 * Fetch only cards for the current result page through Shopify's Section
 * Rendering API, with a bounded concurrency limit and an atomic DOM swap.
 */
(() => {
  if (window.NubsShopRelease?.initialized) return;
  const script = document.currentScript || document.querySelector('[data-nubs-shop-release]');
  const bridge = String(script?.dataset.bridgeUrl || 'https://members.nubsmedia.ca').replace(/\/+$/, '');
  const endpoint = new URL('/catalog/shop', bridge);
  const SECTION_ID = 'nubs-shop-card-fragment';
  const MAX_PARALLEL_CARDS = 4;
  const VALID_HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
  const element = (name, className, text) => {
    const el = document.createElement(name);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = String(text);
    return el;
  };

  const validRows = items => Array.isArray(items) && items.every(item => item &&
    VALID_HANDLE.test(item.handle) &&
    /^(?:NMREL-\d{6})?$/.test(item.nmrel) &&
    /^(?:\d{4}-\d{2}-\d{2})?$/.test(item.releaseDate));

  async function query({mode = 'shop', direction = 'newest', page = 1, filters} = {}, signal) {
    const url = new URL(endpoint);
    url.searchParams.set('mode', mode);
    url.searchParams.set('direction', direction);
    url.searchParams.set('page', String(page));
    if (filters) for (const [key, value] of filters.entries()) {
      if (key.startsWith('filter.')) url.searchParams.append(key, value);
    }
    // Never show a partial index as a complete sorting result.
    for (let attempt = 0; attempt < 4; attempt++) {
      if (signal?.aborted) return null;
      const response = await fetch(url, {mode: 'cors', credentials: 'omit', headers: {Accept: 'application/json'}, signal});
      if (!response.ok && response.status !== 202) throw new Error(`Shop index returned ${response.status}`);
      const data = await response.json();
      if (data.ok && data.ready && validRows(data.items)) return data;
      if (response.status !== 202 || data.ok !== true) throw new Error('Incomplete shop index response.');
      if (attempt < 3) await wait(Math.min(4000, Math.max(1500, Number(data.retryAfter || 3) * 1000)));
    }
    throw new Error('The shop index is still warming.');
  }

  const localePrefix = () => {
    const root = String(window.Shopify?.routes?.root || '/');
    return /^\/(?:fr|es)\/?$/.test(root) ? root.replace(/\/$/, '') : '';
  };

  // No separate pricing, language, images, or MSRP implementation exists here.
  // The fetched section renders the same two Liquid snippets as the normal site.
  const cardCache = new Map();
  function requestCard(handle, signal) {
    if (!VALID_HANDLE.test(handle)) throw new Error('Invalid product handle.');
    const previewId = new URL(location.href).searchParams.get('preview_theme_id');
    const previewKey = previewId && /^\d{1,20}$/.test(previewId) ? previewId : '';
    const key = `${localePrefix()}/${handle}:${previewKey}`;
    const cached = cardCache.get(key);
    if (cached && !cached.signal?.aborted && Date.now() - cached.time < 90_000) return cached.promise;
    if(signal?.aborted)throw new DOMException('Navigation cancelled','AbortError');
    const url = new URL(`${localePrefix()}/products/${encodeURIComponent(handle)}`, location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    // A draft theme's Section Rendering API request must stay within the same
    // Shopify preview context; never add a preview ID on the live storefront.
    if (previewKey) url.searchParams.set('preview_theme_id', previewKey);
    const controller=new AbortController();
    const cancel=()=>controller.abort();signal?.addEventListener('abort',cancel,{once:true});
    const deadline=setTimeout(cancel,12000);
    const promise = fetch(url, {
      credentials: 'same-origin',
      headers: {Accept: 'text/html'},
      signal:controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error(`Shopify card render failed (${response.status})`);
      if (!String(response.headers.get('content-type') || '').includes('text/html')) {
        throw new Error('Shopify did not return a card section.');
      }
      const html = await response.text();
      if (html.length > 160_000) throw new Error('Unexpectedly large Shopify card.');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const container = doc.querySelector('[data-nubs-card-fragment-handle]');
      if (!container || container.dataset.nubsCardFragmentHandle !== handle) {
        throw new Error('Rendered card did not match requested Shopify product.');
      }
      const cards = {};
      for (const [kind, className] of [['home', 'product-card'], ['shop', 'nubs-collection-card']]) {
        const wrapper = container.querySelector(`[data-nubs-card-fragment-kind="${kind}"]`);
        const card = wrapper?.querySelector(`article.${className}`);
        if (!card || (kind === 'home' ? card.dataset.productHandle : card.dataset.nubsCatalogHandle) !== handle) {
          throw new Error(`Missing ${kind} Shopify Liquid card.`);
        }
        if (card.querySelector('script,iframe,object,embed')) throw new Error('Unexpected executable card markup.');
        cards[kind] = card.outerHTML;
      }
      return cards;
    }).catch(error => {
      if(cardCache.get(key)?.promise===promise)cardCache.delete(key);
      throw error;
    }).finally(()=>{clearTimeout(deadline);signal?.removeEventListener('abort',cancel);});
    cardCache.set(key, {time: Date.now(), promise,signal:controller.signal});
    if (cardCache.size > 60) cardCache.delete(cardCache.keys().next().value);
    return promise;
  }

  // Current Shopify cards can be moved, not recreated or re-fetched.
  // This also preserves the exact original image, preferred variant, and MSRP.
  async function liquidCards(rows, kind, currentRoot, signal) {
    const selector = kind === 'home' ? 'article.product-card[data-product-handle]' : 'article.nubs-collection-card[data-nubs-catalog-handle]';
    const attribute = kind === 'home' ? 'productHandle' : 'nubsCatalogHandle';
    const existing = new Map([...currentRoot.querySelectorAll(selector)]
      .filter(card => card.dataset[attribute] && VALID_HANDLE.test(card.dataset[attribute]))
      .map(card => [card.dataset[attribute], card]));
    const nodes = new Array(rows.length);
    const pending = [];
    rows.forEach((row, i) => {
      if (existing.has(row.handle)) nodes[i] = existing.get(row.handle);
      else pending.push({row, i});
    });
    let next = 0;
    const workers = Array.from({length: Math.min(MAX_PARALLEL_CARDS, pending.length)}, async () => {
      while (next < pending.length) {
        if (signal?.aborted) throw new DOMException('Navigation cancelled', 'AbortError');
        const {row, i} = pending[next++];
        const fragment = await requestCard(row.handle, signal);
        const doc = new DOMParser().parseFromString(fragment[kind], 'text/html');
        const card = doc.querySelector(selector);
        if (!card || card.dataset[attribute] !== row.handle) throw new Error('Invalid Shopify-rendered product card.');
        nodes[i] = document.importNode(card, true);
      }
    });
    await Promise.all(workers);
    if (nodes.some(node => !node)) throw new Error('An expected product card was missing.');
    if (kind === 'home') {
      // home-page.js re-sorts New Releases after the update event. Standalone
      // Liquid card fragments don't receive release_timestamp from an outer
      // collection loop, so supply the already validated NMREL index date.
      rows.forEach((row, i) => {
        const stamp = row.releaseDate ? Date.parse(`${row.releaseDate}T00:00:00Z`) / 1000 : 0;
        nodes[i].dataset.releaseTimestamp = String(Number.isFinite(stamp) ? stamp : 0);
      });
    }
    return nodes;
  }

  function updatePager(page, data) {
    let pager = page.querySelector('.nubs-collection-pagination');
    if (!pager) {
      pager = element('nav', 'nubs-collection-pagination');
      pager.setAttribute('aria-label', 'Collection pages');
      page.querySelector('.nubs-collection-results')?.append(pager);
    }
    pager.replaceChildren();
    const totalPages = Math.ceil(data.total / 24);
    if (totalPages <= 1) {pager.hidden = true; return;}
    pager.hidden = false;
    for (const [label, index] of [['Previous', data.page - 1], [`Page ${data.page} of ${totalPages}`, 0], ['Next', data.page + 1]]) {
      if (!index || index < 1 || index > totalPages) {pager.append(element('span', '', label)); continue;}
      const link = element('a', '', label);
      const url = new URL(location.href);
      url.searchParams.set('page', String(index));
      link.href = `${url.pathname}${url.search}`;
      pager.append(link);
    }
  }

  let activeCollectionRequest = 0;
  let collectionController;
  async function updateCollection(page) {
    if (!page?.isConnected || page.matches('[data-nubs-sold-out-pending], [data-nubs-sold-out-ready]')) return;
    const url = new URL(location.href);
    const sort = url.searchParams.get('nubs_sort') || url.searchParams.get('sort_by');
    if (!['release-date-newest', 'release-date-oldest'].includes(sort)) return;
    const id = ++activeCollectionRequest;
    collectionController?.abort();
    collectionController = new AbortController();
    const signal = collectionController.signal;
    const grid = page.querySelector('.nubs-collection-results__grid');
    if (!grid) return;
    let info = page.querySelector('[data-nubs-release-sort-status]');
    if (!info) {info = element('p', 'nubs-collection-page__description'); info.dataset.nubsReleaseSortStatus = ''; grid.before(info);}
    info.textContent = 'Loading products by physical release date…';
    try {
      const data = await query({direction: sort.endsWith('oldest') ? 'oldest' : 'newest', page: Number(url.searchParams.get('page') || 1), filters: url.searchParams}, signal);
      if (!data || signal.aborted || id !== activeCollectionRequest || !grid.isConnected) return;
      const cards = await liquidCards(data.items, 'shop', grid, signal);
      if (signal.aborted || id !== activeCollectionRequest || !grid.isConnected) return;
      grid.replaceChildren(...cards);
      window.NubsCatalogOrder?.revealGrid?.(grid);
      page.querySelectorAll('.nubs-collection-page__stat strong,.nubs-collection-page__result-count')
        .forEach(node => {node.textContent = String(data.total);});
      updatePager(page, data);
      info.textContent = 'Sorted by physical Blu-ray release date.';
    } catch (error) {
      if (!signal.aborted && id === activeCollectionRequest && grid.isConnected) {
        info.textContent = 'Release-date sorting is temporarily unavailable. Normal Shopify browsing is still available.';
        console.warn('[Nub Shop] Could not load complete Shopify-rendered cards', error);
      }
    }
  }

  let activeHomeRequest = 0;
  let homeController;
  const showHomeCards = () => document.documentElement.classList.remove('nubs-new-releases-pending');
  async function updateHome() {
    const section = document.getElementById('new-releases');
    if (!section?.isConnected || !section.closest('main')) return;
    const id = ++activeHomeRequest;
    homeController?.abort();
    homeController = new AbortController();
    const signal = homeController.signal;
    // A restored page must not keep controls bound to an aborted old source.
    // Its currently visible Liquid cards are the fallback until refresh succeeds.
    if (section.nubsCarouselSource) {
      delete section.nubsCarouselSource;
      document.dispatchEvent(new CustomEvent('nubs:product-cards-updated', {detail:{id:'new-releases'}}));
    }
    // Never keep the homepage hidden indefinitely during a cold cache build.
    // On timeout retain the original Shopify-rendered list for this visit,
    // instead of allowing a late asynchronous swap after it becomes visible.
    const fallbackTimer = setTimeout(() => homeController?.signal === signal && homeController.abort(), 12000);
    try {
      const data = await query({mode: 'new-releases'}, signal);
      if (!data || signal.aborted || id !== activeHomeRequest || !section.isConnected) return;
      if (!data.items.length) { section.hidden = true; showHomeCards(); return; }
      const track = section.querySelector('[data-carousel-track]');
      if (!track) return;
      const rows=data.items.slice(0,25);
      const buffer=new Map([...track.querySelectorAll('article.product-card[data-product-handle]')].map(card=>[card.dataset.productHandle,card]));
      const flights=new Map();
      const active=()=>!signal.aborted && id===activeHomeRequest && section.isConnected;
      const source={total:rows.length,async getPage(start,end){
        if(!active())throw new DOMException('Navigation cancelled','AbortError');
        const key=`${start}:${end}`;
        if(flights.has(key))return flights.get(key);
        const job=liquidCards(rows.slice(start,end),'home',{querySelectorAll:()=>[...buffer.values()]},signal).then(cards=>{
          if(!active())throw new DOMException('Navigation cancelled','AbortError');
          cards.forEach(card=>buffer.set(card.dataset.productHandle,card));return cards;
        }).finally(()=>{if(flights.get(key)===job)flights.delete(key);});
        flights.set(key,job);return job;
      }};
      const count=window.matchMedia('(max-width: 720px)').matches?2:window.matchMedia('(max-width: 1000px)').matches?4:5;
      // Only the first visible screen blocks reveal. Later carousel pages retain
      // the exact NMREL order and fetch their original Liquid cards on demand.
      const cards=await source.getPage(0,Math.min(count,rows.length));
      if(!active())return;
      section.nubsCarouselSource=source;
      track.replaceChildren(...cards);
      const nextEnd = Math.min(count * 2, rows.length);
      if (nextEnd > count && !navigator.connection?.saveData &&
          !['slow-2g', '2g'].includes(navigator.connection?.effectiveType)) {
        const warmNext = () => {
          if (active() && document.visibilityState === 'visible') source.getPage(count, nextEnd).catch(() => {});
        };
        if ('requestIdleCallback' in window) requestIdleCallback(warmNext);
        else setTimeout(warmNext, 600);
      }
      section.hidden = false;
      document.dispatchEvent(new CustomEvent('nubs:product-cards-updated', {detail: {id: 'new-releases'}}));
      // home-page.js finishes sorting/measuring on the next animation frame.
      // Reveal after that work, so neither a wrong card list nor a half-sorted
      // carousel can appear during initial paint.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (id === activeHomeRequest && section.isConnected) showHomeCards();
      }));
    } catch (error) {
      if (!signal.aborted) console.warn('[Nub home] Retaining original Shopify cards.', error);
      if (id === activeHomeRequest) showHomeCards();
    } finally {
      clearTimeout(fallbackTimer);
      if (id === activeHomeRequest && signal.aborted) showHomeCards();
    }
  }

  async function sortSoldOut(cards, sort, signal) {
    const dates = new Map();
    const batches = [];
    for (let i = 0; i < cards.length; i += 48) {
      batches.push(cards.slice(i, i + 48).map(card => card.dataset.nubsCatalogHandle));
    }
    for (let start = 0; start < batches.length; start += 3) {
      await Promise.all(batches.slice(start, start + 3).map(async handles => {
        if (!handles.every(handle => VALID_HANDLE.test(handle))) throw new Error('Invalid product handle.');
        const url = new URL(endpoint);
        url.searchParams.set('mode', 'release-dates');
        url.searchParams.set('handles', [...handles].sort().join(','));
        const response = await fetch(url, {mode: 'cors', credentials: 'omit', signal});
        if (!response.ok) throw new Error('Release dates are unavailable.');
        const data = await response.json();
        if (data.mode !== 'release-dates' || !data.ready || !validRows(data.items)) {
          throw new Error('Release dates are unavailable.');
        }
        for (const row of data.items) dates.set(row.handle, row);
      }));
    }
    return [...cards].sort((left, right) => {
      const a = dates.get(left.dataset.nubsCatalogHandle);
      const b = dates.get(right.dataset.nubsCatalogHandle);
      const aDate = a?.releaseDate || '';
      const bDate = b?.releaseDate || '';
      if (Boolean(aDate) !== Boolean(bDate)) return aDate ? -1 : 1;
      const order = sort.endsWith('oldest') ? aDate.localeCompare(bDate) : bDate.localeCompare(aDate);
      return order || (a?.nmrel || '').localeCompare(b?.nmrel || '') ||
        left.dataset.nubsCatalogHandle.localeCompare(right.dataset.nubsCatalogHandle);
    });
  }

  function onPage() {
    const page = document.querySelector('[data-nubs-collection]');
    if (page && location.pathname.replace(/^\/(fr|es)/, '') === '/collections/all') updateCollection(page);
    if (location.pathname === '/' || /^\/(fr|es)\/?$/.test(location.pathname)) updateHome();
  }
  document.addEventListener('nubs:beforepagechange',()=>{activeHomeRequest++;activeCollectionRequest++;homeController?.abort();collectionController?.abort();});
  window.addEventListener('pagehide',()=>{homeController?.abort();collectionController?.abort();});
  window.addEventListener('pageshow',event=>{if(event.persisted)onPage();});
  document.addEventListener('nubs:pagechange', onPage);
  document.addEventListener('nubs:shop-section-updated', onPage);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onPage, {once: true});
  else onPage();
  window.NubsShopRelease = {initialized: true, updateCollection, updateHome, sortSoldOut};
})();
